import {
  Directive,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  ElementRef,
  Renderer2,
  HostListener,
} from '@angular/core';
import {
  DfSelectSingleFilter,
  DfSelectSingleOption,
} from './df-select-single-filter.model';

const PAGE_SIZE = 20;

/**
 * Pure attribute directive: single-select dropdown with search and lazy-loaded list.
 * Use as: <div df-select-single [filter]="filter" (filterChange)="onFilterChange($event)"></div>
 * Requires df-select-single.styles.scss to be imported in app global styles.
 */
@Directive({
    selector: '[df-select-single]',
    standalone: false
})
export class DfSelectSingleDirective implements OnInit, OnChanges, OnDestroy {
  @Input() filter!: DfSelectSingleFilter;
  /**
   * When true, the closed trigger shows the filter label (e.g. "IRS Submissions")
   * even if an option is selected — used for auto-selected defaults until the
   * user picks a value themselves (then show "N Checked").
   */
  @Input() showPlaceholderLabel = false;
  @Output() filterChange = new EventEmitter<DfSelectSingleFilter>();

  searchItem = '';
  searchValue = '';
  filterLabel = '';
  filterTotalDrpList: DfSelectSingleOption[] = [];
  filterCachedDrpList: DfSelectSingleOption[] = [];
  isOpen = false;
  private listIndx = 0;
  private scrollListener: (() => void) | null = null;

  private rootEl: HTMLElement | null = null;
  private loadingEl: HTMLElement | null = null;
  private selectWrapEl: HTMLElement | null = null;
  private triggerEl: HTMLElement | null = null;
  private panelEl: HTMLElement | null = null;
  private listUlEl: HTMLElement | null = null;
  private searchInputEl: HTMLInputElement | null = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    if (!this.filter) {
      this.filter = { selected: [] };
    }
    this.syncFromFilter(this.filter);
    this.buildDOM();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const f = changes['filter']?.currentValue as DfSelectSingleFilter | undefined;
    if (f) {
      this.filterLabel = f.label ?? '';
      this.syncFromFilter(f);
    }
    if (f || changes['showPlaceholderLabel']) {
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.detachScrollListener();
    if (this.rootEl && this.el.nativeElement.contains(this.rootEl)) {
      this.renderer.removeChild(this.el.nativeElement, this.rootEl);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.isOpen && target && !this.el.nativeElement.contains(target)) {
      this.isOpen = false;
      this.render();
    }
  }

  private syncFromFilter(f: DfSelectSingleFilter): void {
    if (!f) return;
    if (!Array.isArray(f.selected)) f.selected = [];
    const values = f.values ?? [];
    this.filterTotalDrpList = values.map((v) => ({ ...v }));
    this.filterCachedDrpList = this.filterTotalDrpList.slice(0, PAGE_SIZE);
    this.searchItem = '';
    this.searchValue = '';
    this.listIndx = 0;
    const selected = f.selected;
    this.filterTotalDrpList.forEach((item) => {
      item.isChecked = selected.some((s) => s.VALUE === item.VALUE);
    });
  }

  private buildDOM(): void {
    const host = this.el.nativeElement;
    this.rootEl = this.renderer.createElement('div');
    this.renderer.addClass(this.rootEl, 'df-select-single-host');

    this.loadingEl = this.renderer.createElement('div');
    this.renderer.addClass(this.loadingEl, 'gt-filter-loading');
    const spinner = this.renderer.createElement('i');
    this.renderer.addClass(spinner, 'fa');
    this.renderer.addClass(spinner, 'fa-spinner');
    this.renderer.addClass(spinner, 'fa-spin');
    this.renderer.appendChild(this.loadingEl, spinner);

    this.selectWrapEl = this.renderer.createElement('div');
    this.renderer.addClass(this.selectWrapEl, 'df-select');

    this.triggerEl = this.renderer.createElement('div');
    this.renderer.addClass(this.triggerEl, 'df-gt-lazy-load-drp-btn');
    this.renderer.addClass(this.triggerEl, 'dropdown-toggle');
    this.renderer.setAttribute(this.triggerEl, 'data-toggle', 'dropdown');
    this.renderer.listen(this.triggerEl, 'click', (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleDropdown();
    });
    const triggerContent = this.renderer.createElement('div');
    this.renderer.addClass(triggerContent, 'df-trigger-content');
    this.renderer.appendChild(this.triggerEl, triggerContent);
    const caret = this.renderer.createElement('span');
    this.renderer.addClass(caret, 'caret');
    this.renderer.addClass(caret, 'caret-style');
    this.renderer.appendChild(this.triggerEl, caret);

    this.panelEl = this.renderer.createElement('div');
    this.renderer.addClass(this.panelEl, 'df-gt-lazy-dropdown-list');
    this.renderer.addClass(this.panelEl, 'dropdown-menu');

    const actions = this.renderer.createElement('div');
    this.renderer.addClass(actions, 'df-dropdown-actions');
    const uncheckLi = this.renderer.createElement('li');
    this.renderer.listen(uncheckLi, 'click', (e: Event) => {
      e.stopPropagation();
      this.dfUnCheckAll();
    });
    const uncheckIcon = this.renderer.createElement('i');
    this.renderer.addClass(uncheckIcon, 'fa');
    this.renderer.addClass(uncheckIcon, 'fa-times');
    this.renderer.appendChild(uncheckLi, uncheckIcon);
    const uncheckSpan = this.renderer.createElement('span');
    this.renderer.addClass(uncheckSpan, 'action-label');
    this.renderer.appendChild(uncheckSpan, this.renderer.createText('UnCheck All'));
    this.renderer.appendChild(uncheckLi, uncheckSpan);
    this.renderer.appendChild(actions, uncheckLi);

    const searchBar = this.renderer.createElement('div');
    this.renderer.addClass(searchBar, 'sticky-search-bar');
    this.searchInputEl = this.renderer.createElement('input');
    this.renderer.setAttribute(this.searchInputEl, 'type', 'text');
    this.renderer.addClass(this.searchInputEl, 'form-control');
    this.renderer.setAttribute(this.searchInputEl, 'placeholder', 'Search...');
    this.renderer.listen(this.searchInputEl, 'input', () => {
      this.searchItem = this.searchInputEl?.value ?? '';
      this.dfSearching(this.searchItem);
      this.render();
    });
    this.renderer.listen(this.searchInputEl, 'click', (e: Event) => e.stopPropagation());
    this.renderer.appendChild(searchBar, this.searchInputEl);

    this.listUlEl = this.renderer.createElement('ul');
    this.renderer.addClass(this.listUlEl, 'list-ul');
    this.renderer.listen(this.listUlEl, 'scroll', () => this.onListScroll());

    this.renderer.appendChild(this.panelEl, actions);
    this.renderer.appendChild(this.panelEl, searchBar);
    this.renderer.appendChild(this.panelEl, this.listUlEl);

    this.renderer.appendChild(this.selectWrapEl, this.triggerEl);
    this.renderer.appendChild(this.selectWrapEl, this.panelEl);

    this.renderer.appendChild(this.rootEl, this.loadingEl);
    this.renderer.appendChild(this.rootEl, this.selectWrapEl);
    this.renderer.appendChild(host, this.rootEl);

    this.attachScrollListener();
    if (this.filter) {
      this.syncFromFilter(this.filter);
    }
    this.render();
  }

  private render(): void {
    if (!this.rootEl) return;
    const filter = this.filter;
    if (!filter) {
      if (this.loadingEl) this.renderer.setStyle(this.loadingEl, 'display', 'none');
      if (this.selectWrapEl) this.renderer.setStyle(this.selectWrapEl, 'display', 'none');
      return;
    }

    const loading = filter.loading;
    if (this.loadingEl) {
      this.renderer.setStyle(this.loadingEl, 'display', loading ? 'flex' : 'none');
    }
    if (this.selectWrapEl) {
      this.renderer.setStyle(this.selectWrapEl, 'display', loading ? 'none' : 'inline-block');
    }

    if (loading) return;

    const triggerContent = this.triggerEl?.querySelector('.df-trigger-content');
    if (triggerContent) {
      triggerContent.innerHTML = '';
      const selectedLen = filter.selected?.length ?? 0;
      const showLabel =
        selectedLen === 0 || (this.showPlaceholderLabel && selectedLen > 0);
      if (showLabel) {
        const div = this.renderer.createElement('div');
        this.renderer.addClass(div, 'df-placeholder');
        this.renderer.addClass(div, 'truncate');
        const span = this.renderer.createElement('span');
        this.renderer.appendChild(
          span,
          this.renderer.createText(this.filterLabel || 'Select'),
        );
        this.renderer.appendChild(div, span);
        this.renderer.appendChild(triggerContent, div);
      } else {
        const div = this.renderer.createElement('div');
        this.renderer.addClass(div, 'df-selected-text');
        const span = this.renderer.createElement('span');
        this.renderer.appendChild(span, this.renderer.createText(String(selectedLen)));
        this.renderer.appendChild(div, span);
        this.renderer.appendChild(div, this.renderer.createText(' Checked'));
        this.renderer.appendChild(triggerContent, div);
      }
    }

    if (this.panelEl) {
      if (this.isOpen) {
        this.renderer.addClass(this.panelEl, 'show');
      } else {
        this.renderer.removeClass(this.panelEl, 'show');
      }
    }

    if (this.listUlEl) {
      this.listUlEl.innerHTML = '';
      if (this.filterCachedDrpList.length > 0) {
        this.filterCachedDrpList.forEach((filterList, i) => {
          const li = this.renderer.createElement('li');
          this.renderer.addClass(li, 'df-gt-lazy-load-list-trunc');
          this.renderer.listen(li, 'click', (e: Event) => {
            e.stopPropagation();
            this.dfSelectedListItem(filterList, i, filterList.isChecked ?? false);
          });
          if (filterList.isChecked) {
            const icon = this.renderer.createElement('i');
            this.renderer.addClass(icon, 'fa');
            this.renderer.addClass(icon, 'fa-check');
            this.renderer.appendChild(li, icon);
          }
          this.renderer.appendChild(li, this.renderer.createText(filterList.LABEL ?? ''));
          this.renderer.appendChild(this.listUlEl!, li);
        });
      } else {
        const li = this.renderer.createElement('li');
        this.renderer.addClass(li, 'df-no-results');
        const i = this.renderer.createElement('i');
        this.renderer.appendChild(i, this.renderer.createText(`No search results found for "${this.searchItem}"`));
        this.renderer.appendChild(li, i);
        this.renderer.appendChild(this.listUlEl!, li);
      }
    }
  }

  private toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    this.render();
  }

  private attachScrollListener(): void {
    if (!this.listUlEl) return;
    this.scrollListener = (): void => this.fetchMoreDfSelectList();
    this.listUlEl.addEventListener('scroll', this.scrollListener);
  }

  private detachScrollListener(): void {
    if (this.listUlEl && this.scrollListener) {
      this.listUlEl.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
    }
  }

  private onListScroll(): void {
    const el = this.listUlEl;
    if (!el) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 4) {
      this.fetchMoreDfSelectList();
    }
  }

  private resetList(): void {
    this.listIndx = 0;
    this.filterCachedDrpList = this.filterTotalDrpList.slice(0, PAGE_SIZE);
  }

  private dfSearching(value: string): void {
    this.searchValue = (value || '').toLowerCase();
    if (this.searchValue.length > 0) {
      const searchResults = this.filterTotalDrpList.filter((itr) => {
        const label = (itr.LABEL ?? '').toString().toLowerCase();
        return label.indexOf(this.searchValue) !== -1;
      });
      this.filterCachedDrpList = searchResults.slice(0, PAGE_SIZE);
    } else {
      this.resetList();
    }
    this.listIndx = this.filterCachedDrpList.length;
  }

  private fetchMoreDfSelectList(): void {
    this.listIndx = this.filterCachedDrpList.length;
    const r = PAGE_SIZE;
    if (this.searchValue.length > 0) {
      const searchResults = this.filterTotalDrpList.filter((itr) => {
        const label = (itr.LABEL ?? '').toString().toLowerCase();
        return label.indexOf(this.searchValue) !== -1;
      });
      const next = searchResults.slice(this.listIndx, this.listIndx + r);
      this.filterCachedDrpList = [...this.filterCachedDrpList, ...next];
    } else {
      const next = this.filterTotalDrpList.slice(this.listIndx, this.listIndx + r);
      this.filterCachedDrpList = [...this.filterCachedDrpList, ...next];
    }
    this.render();
  }

  private dfSelectedListItem(_selectedDfList: DfSelectSingleOption, index: number, checkedFlag: boolean): void {
    if (!this.filter) return;
    if (checkedFlag === true) {
      this.filterCachedDrpList[index].isChecked = false;
    } else {
      this.filterTotalDrpList.forEach((i) => {
        if (i.isChecked) {
          i.isChecked = false;
          this.removeFromSelected(i.VALUE);
        }
      });
      this.filterCachedDrpList[index].isChecked = true;
    }
    this.filterCachedDrpList.forEach((item) => {
      if (item.isChecked === true) {
        if (!this.filter.selected.some((s) => s.VALUE === item.VALUE)) {
          this.filter.selected.push(item);
        }
      } else {
        this.removeFromSelected(item.VALUE);
      }
    });
    this.filterChange.emit(this.filter);
    this.isOpen = false;
    this.render();
  }

  private removeFromSelected(value: string | number): void {
    if (!this.filter?.selected) return;
    const idx = this.filter.selected.findIndex((s) => s.VALUE === value);
    if (idx !== -1) this.filter.selected.splice(idx, 1);
  }

  private dfUnCheckAll(): void {
    if (!this.filter) return;
    this.filterTotalDrpList.forEach((eachItem) => {
      if (eachItem.isChecked) eachItem.isChecked = false;
    });
    this.filter.selected = [];
    this.filterChange.emit(this.filter);
    this.render();
  }

  private dfCheckAll(): void {
    if (!this.filter) return;
    this.filterTotalDrpList.forEach((eachItem) => {
      if (eachItem.isChecked !== true) {
        eachItem.isChecked = true;
        this.filter.selected.push(eachItem);
      }
    });
    this.filterChange.emit(this.filter);
    this.render();
  }
}
